import { Injectable } from '@angular/core';
import { FirestoreService } from './firestore-service.service';
import { GeminiService } from './gemini.service';
import { SearchService } from './search.service';
import { doc, setDoc, writeBatch } from 'firebase/firestore';

@Injectable({
  providedIn: 'root'
})
export class OgragService {
  firstPrompt = "Sentence is set of two terms and a relation between them. Based on the attached text create a list displaying one term, the second term which is in relation to the first one, and the name of the relation, called a sentence. Bring only sentences that are relevant and meaningful and important from the perspective of business development. Don't provide duplicated sentences, find new sentences only. Provide json in the following format: [{'first_term': <first term>, 'second_term': <second term>, 'relation': <relation name>}, ...] Don't provide anything else beyond the json.\n\n\n"
  firstImplicationPrompt = "Sentence is set of two terms and a where the second term is a consequence or implication of the first term. First term and second terms should be completed with short context clarifying their meaning. Based on the attached text create a list displaying one term, the second term which is in logical consequence to the first one, and the name of the relation. Return only sentences that are relevant and meaningful and important from the perspective of business development, and are in cause and effect relation. Don't provide duplicated sentences, find new sentences only. Provide json in the following format: [{'first_term': <first term>, 'second_term': <second term>, 'relation': <relation name>}, ...] Don't provide anything else beyond the json.\n\n\n"
  nextPrompt = "Based on the attached list of sentences create a array displaying a sentence as the first term, another sentence as the second term which is in relation to the first one, and the name of the relation between them. So find relations between sentences. Don't provide duplicated results, don't repeat the same relation between the same terms. Provide json in the following format: [{'first_term': <first term>, 'second_term': <second term>, 'relation': <relation name>}, ...] Don't provide anything else besides the json with new meta sentences.\n\n\n"
  nextImplicationPrompt = "Based on the attached list of sentences create a array displaying a sentence as the first term, another sentence as the second term which is in logical consequence or implication of the first one, and the name of the relation between them. First term and second terms should be completed with short context clarifying their meaning.  So find relations between sentences which are in cause and effect relation. Don't provide duplicated results, don't repeat the same relation between the same terms. Provide json in the following format: [{'first_term': <first term>, 'second_term': <second term>, 'relation': <relation name>}, ...] Don't provide anything else besides the json with new meta sentences.\n\n\n"
  reasoningGoalPrompt = "Define reasoning goal based on the attached content:\n\n\n"
  reasoningPrompt = "based on this reasoning goal draw conclusions from the attached file. display those conclusions in the json format: ['first_term': <premises and arguments supporting conclusion>, 'second_term': <conclusion interested from the perspective of reasoning goal>, 'relation': <description of logical relation between first_term and second_term. Don't provide any additional comments nor summaries. Provide plain and correct json format. Avoid providing duplicated sentences. Avoid sentences idem per idem, where first term is the same as the second term.\n\n\nReasoning goal: \n\n"
  userId = ""
  organizationKey = ""
  projectKey = ""
  llmLimit = 100
  enableGoogleSearchGrounding = true
  modelKey = "gemini-2.5-flash-lite"
  temperature = 0
  statementPrompt = "Give me list of informative statements that can be found in this text. don't include citations. Statement consists of 1 to 4 sentences providing essential information, it has meaning if read standalone and it is not a continuation of another sentence. Replace pronouns, with the nouns they refer to or explain what they refer to. For example term 'in such situation' replace with description of what it means. If the sentence refers to other part of the text, include also the part of the text it refers to, so reader can understand the context. Return it in json format [{'statement': <sentence from the text>, ...] in language of the text. Don't provide anything else besides the json."


  
  constructor(private firestoreService: FirestoreService, private geminiService: GeminiService, private search: SearchService) { }

  async getResponse(organizationKey: string, projectKey: string, prompt: string, userId: string, llmLimit: number, enableGoogleSearchGrounding: boolean = true, modelKey: string = "gemini-2.5-flash", temperature: number = 0) {
    return this.geminiService.getGeminiResponseSimple(organizationKey, projectKey, userId, prompt, llmLimit, enableGoogleSearchGrounding, modelKey, temperature).then
    (response => {
      return response
    }).catch(error => {
      console.error("Error getResponse:", error)
      return {responseText: "Error getting response"}
    })
  }

  async createOntologies(userId: string, organizationKey: string, projectKey: string, content: {"key": string, "content": string}[], documents: {"key": string, "content": string}[]) {
    const ontologies: any[] = []
    // create ontologies for documents
    let contents: {"key": string, "content": string}[] = []
    const maxLength = 10000

    for (const document of documents) {
      let content = await this.firestoreService.getDocumentContent(organizationKey, document.key)
      if (!content || !content.textContent || content.textContent.length < 10) {
        continue
      }
      
      let text = content.textContent
      const documentKey = document.key // Store the key for use in the chunks
      if (text.length > maxLength) {
        // Determine the number of chunks needed
        const numChunks = Math.ceil(text.length / maxLength)
        
        // Split the content into chunks
        for (let i = 0; i < numChunks; i++) {
          const start = i * maxLength
          const end = start + maxLength
          const chunk = text.substring(start, end)
          
          // Add each chunk with an identifier to the contents array
          contents.push({
            "key": `${documentKey}_chunk${i + 1}`, // Unique key for each chunk
            "content": chunk
          })
        }
      } else {
        // If the content is within the limit, add it as a single item
        contents.push({
          "key": documentKey, 
          "content": text
        })
      }
    }

    for (const contentItem of content) {
      contents.push({"key": contentItem.key, "content": contentItem.content})
    }

// Value: Array of JSON objects (from all chunks of that document)
const consolidatedResults: Map<string, any[]> = new Map();
let promises: Promise<any>[] = []
for (const contentItem of contents) {
  let documentKey: string;
  const itemKey = contentItem.key;
  if (itemKey.includes("_chunk")) {
    // If it's a chunk, extract the original key (everything before the last "_chunk")
    // This assumes your document keys don't naturally contain "_chunk" 
    documentKey = itemKey.substring(0, itemKey.lastIndexOf("_chunk"));
  } else {
    // If it's a non-chunked document, the key is the original document key
    documentKey = itemKey;
  }
  // 1. Create the JSON Ontology for the current chunk/document
  promises.push(this.createOntology(organizationKey, projectKey, this.firstImplicationPrompt, userId, contentItem.content, documentKey))
}
const responses = await Promise.all(promises)
for (const res of responses) {
  const response = res.jsonResponse
  if (!response || response.length === 0) {
    continue;
  }
  // 2. Add an iteration property (as you did)
  response.forEach((item: any) => {
    item.iteration = 0;
  });

  const documentKey = res.documentKey
    // 2. Add an iteration property (as you did)
    response.forEach((item: any) => {
      item.iteration = 0;
    });
  
    // 3. Consolidate the results by the determined documentKey
    if (consolidatedResults.has(documentKey)) {
      // If the document key is already in the map, append the new results
      consolidatedResults.get(documentKey)!.push(...response);
    } else {
      // Otherwise, initialize the array with the current results
      consolidatedResults.set(documentKey, response);
    }
}

const maxRows = 100 // max number of rows in one of ontology
for (const [documentKey, initialOntologies] of consolidatedResults.entries()) {
  this.firestoreService.deleteOntology(organizationKey, documentKey)
  // Use the consolidated results as the starting point
  let response = initialOntologies 
  if (!response || response.length == 0) {
    continue
  }
  // We use the initial consolidated ontologies for the first run of the secondary analysis
  let jsonResponse: any[] = []
  jsonResponse = jsonResponse.concat(response)
  // Secondary Analysis Iterations
  if (jsonResponse.length > 0) {
    for (let i = 1; i < 3; i++) {
      const on = jsonResponse.filter((item: any) => item.iteration == i-1)
      const ontologyChunks = this.splitOntologyIntoChunks(on, maxRows)
      for (const chunk of ontologyChunks) {
        const textResponse: string = this.convertJsonToSentences(chunk)
        const res = await this.createOntology(organizationKey, projectKey, this.nextImplicationPrompt, userId, textResponse, documentKey)
        console.log("createOntology res: ",res)
        const chunkResponse = res.jsonResponse

        if (!chunkResponse || chunkResponse.length == 0) {
          continue
        }
        chunkResponse.forEach((item: any) => {
          item.iteration = i
          item.score = 1
        })
        jsonResponse = jsonResponse.concat(chunkResponse)
      }
    }
  }
  // 2. Save the final consolidated and analyzed ontologies, using the original documentKey
  await this.firestoreService.addOntology(userId, organizationKey, projectKey, documentKey, "content", jsonResponse)
  ontologies.push(jsonResponse)
}
    return ontologies
  }





  async addOntologyEmbeddings(
    userId: string, 
    organizationKey: string, 
    projectKey: string, 
    documentKey: string, 
    ontology: OntologyStatement[], 
    maxLength: number, 
  ) {
    const db = this.firestoreService.getApps();
    const limitedOntology = ontology.slice(0, maxLength);
    
    const vectorsFull = [];
    const vectorsFirstTerm = [];
    const vectorsSecondTerm = [];
  
    // 1. Process Embeddings & Prepare Data
    // We do this first to separate logic from DB writes
    for (const item of limitedOntology) {
      let prompt = ""
      if (item.first_term && item.relation && item.second_term && item.first_term.length > 10 && item.second_term.length > 10 && item.relation.length > 5) {
       prompt = `${item.first_term} ${item.relation} ${item.second_term}`;
      }

      prompt = this.processText(prompt)
      const firstTermOriginal = item.first_term
      const secondTermOriginal = item.second_term
      item.first_term = this.processText(item.first_term)
      item.second_term = this.processText(item.second_term)

      const [emb, first, second] = await Promise.all([
        this.getEmbedding(prompt),
        this.getEmbedding(item.first_term),
        this.getEmbedding(item.second_term)
      ]);

      let embFull = []
      let embFirstTerm = []
      let embSecondTerm = []
      if (emb && emb.length > 0) {
        embFull = emb[0].values || []
      }
      if (first && first.length > 0) {
        embFirstTerm = first[0].values || []
      }
      if (second && second.length > 0) {
        embSecondTerm = second[0].values || []
      }
  
      const data = {
        userId, organizationKey, projectKey, documentKey,
        embedding: embFull,
        firstTermEmbedding: embFirstTerm,
        secondTermEmbedding: embSecondTerm,
        dateOfCreation: Date.now(),
        iteration: item.iteration || 0,
        firstTerm: item.first_term || "",
        firstTermOriginal: firstTermOriginal || "",
        relation: item.relation || "",
        secondTerm: item.second_term || "",
        secondTermOriginal: secondTermOriginal || "",
        key: item.key,
        score: item.score || 1
      };
  
      (item as any)._preparedData = data;
      vectorsFull.push({ id: item.key, values: data.embedding });
      vectorsFirstTerm.push({ id: item.key, values: data.firstTermEmbedding });
      vectorsSecondTerm.push({ id: item.key, values: data.secondTermEmbedding });
    }
  
    // 2. Individual Writes with Throttling
    // We process in "concurrency buckets" of 10 to avoid 
    // "Transaction too big" and "Too many outgoing requests"
    const CONCURRENCY_LIMIT = 10;
    for (let i = 0; i < limitedOntology.length; i += CONCURRENCY_LIMIT) {
      const bucket = limitedOntology.slice(i, i + CONCURRENCY_LIMIT);

      console.log("batching embeddings: ", i)
      
      // Execute 10 writes in parallel
      await Promise.all(bucket.map(item => {
        const docRef = doc(db, "organizations", organizationKey, "ontologyStatements", item.key);
        return setDoc(docRef, (item as any)._preparedData);
      }));
  
      console.log(`Uploaded ${i + bucket.length} / ${limitedOntology.length} documents...`);
      
      // Optional: Small delay (50ms) to let the network breathe
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    console.log("syncing vectors")
  
    // 3. Sync to Vector Service
    // Break these into smaller chunks too if they fail
    const token = await this.search.getIdToken()
    await this.syncVectors(organizationKey, vectorsFull, token, "full");
    await this.syncVectors(organizationKey, vectorsFirstTerm, token, "first_term");
    await this.syncVectors(organizationKey, vectorsSecondTerm, token, "second_term");
  }
  
  // Helper to chunk vector upserts (if needed)
  async syncVectors(orgKey: string, vectors: any[], token: string, type: string) {

    const CHUNK_SIZE = 100;
    for (let i = 0; i < vectors.length; i += CHUNK_SIZE) {
      const chunk = vectors.slice(i, i + CHUNK_SIZE);
      await this.firestoreService.upsertVectorPerDocument(orgKey, chunk, token, true, type);
    }
  }




  async addOntologyEmbeddings2(userId: string, organizationKey: string, projectKey: string, documentKey: string, ontology: OntologyStatement[], maxLength: number, token: string) {
    let index = 0
    let vectorsFull: {"id": string, "values": number[]}[] = []
    let vectorsFirstTerm: {"id": string, "values": number[]}[] = []
    let vectorsSecondTerm: {"id": string, "values": number[]}[] = []
    for (const item of ontology) {
      const prompt = item.first_term + " " + item.relation + " " + item.second_term
      const emb = await this.getEmbedding(prompt)
      const first = await this.getEmbedding(item.first_term)
      const second = await this.getEmbedding(item.second_term)
      const embedding = emb[0].values
      const firstTermEmbedding = first[0].values
      const secondTermEmbedding = second[0].values
      const id = await this.firestoreService.addOntologyEmbeddings(userId, organizationKey, projectKey, documentKey, embedding, firstTermEmbedding, secondTermEmbedding, item)
      vectorsFull.push({"id": id, "values": embedding})
      vectorsFirstTerm.push({"id": id, "values": firstTermEmbedding})
      vectorsSecondTerm.push({"id": id, "values": secondTermEmbedding})
      index++
      if (index >= maxLength) {
        break
      }
    }
    if (vectorsFull.length > 0) {
      await this.firestoreService.upsertVectorPerDocument(organizationKey, vectorsFull, token, true, "full")
    }
    if (vectorsFirstTerm.length > 0) {
      await this.firestoreService.upsertVectorPerDocument(organizationKey, vectorsFirstTerm, token, true, "first_term")
    }
    if (vectorsSecondTerm.length > 0) {
      await this.firestoreService.upsertVectorPerDocument(organizationKey, vectorsSecondTerm, token, true, "second_term")
    }
  }

  async getOntologyEmbeddings(organizationKey: string, id: string) {
    const embeddings = await this.firestoreService.getOntologyEmbeddings(organizationKey, id)
    return embeddings
  }

  async getSimilarOntologies(organizationKey: string, queryVector: number[], topK: number, threshold: number, statementType: string[]) {
    let result: any[] = []
    for (const type of statementType) {
      const r = await this.search.queryVectorOntology(organizationKey, queryVector, topK, threshold, type)
      result = result.concat(r)
    }
    return result
  }

  async createOntology(organizationKey: string, projectKey: string, prompt: string, userId: string, content: string, documentKey: string) {
    const response = await this.getResponse(organizationKey, projectKey, prompt + content, userId, this.llmLimit)
    let jsonResponse = this.search.extractJsonFromString(response.responseText)
    if (!jsonResponse || jsonResponse.length == 0) {
      const response = await this.getResponse(organizationKey, projectKey, prompt + content, userId, this.llmLimit)
      jsonResponse = this.search.extractJsonFromString(response.responseText)
    }
    jsonResponse.forEach((item: any) => {
      item.key = this.firestoreService.generateUID("statement-")
    })
    const res = {
      "documentKey": documentKey,
      "jsonResponse": jsonResponse,
    }
    return res
  }

  splitOntologyIntoChunks(ontology: any[], maxRows: number) {
    const chunks: any[] = []
    for (let i = 0; i < ontology.length; i += maxRows) {
      chunks.push(ontology.slice(i, i + maxRows))
    }
    return chunks
  }

  async conductReasoning(userId: string, organizationKey: string, projectKey: string, content: {"key": string, "content": string}[], documents: {"key": string, "content": string}[]) {
    let ontologies: any[] = []
    for (const document of documents) {
      let ontology = await this.firestoreService.getOntology(organizationKey, document.key)
      let filteredOntology = ontology.filter((item: any) => item.iteration == 2)
      if (!filteredOntology || filteredOntology.length == 0) {
        continue
      }
    //  const c = await this.firestoreService.getDocumentContent(organizationKey, document.key) 
    //  const content = c.textContent + "\n\n\n" + JSON.stringify(ontology)
      const textContent = this.convertJsonToSentences(filteredOntology)
      const reasoningGoal = await this.createReasoningGoal(organizationKey, projectKey, userId, textContent)
      const prompt = this.reasoningPrompt + reasoningGoal + "\n\n\nContent to reason about: \n\n" + textContent
      const response = await this.getResponse(organizationKey, projectKey, prompt, userId, this.llmLimit, false, "gemini-3-pro-preview")
      const jsonResponse = this.search.extractJsonFromString(response.responseText)
      jsonResponse.forEach((item: any) => {
        item.iteration = 100
      })
      ontology = ontology.concat(jsonResponse)
      await this.firestoreService.addOntology(userId, organizationKey, projectKey, document.key, "document", ontology)
      ontologies.push(ontology)
    }
    for (const contentItem of content) {
      let ontology = await this.firestoreService.getOntology(organizationKey, contentItem.key)
      let filteredOntology = ontology.filter((item: any) => item.iteration == 2)
      if (!filteredOntology || filteredOntology.length == 0) {
        continue
      }
      const textContent = this.convertJsonToSentences(filteredOntology)
    //  const content = contentItem.content + "\n\n\n" + JSON.stringify(ontology)
      const reasoningGoal = await this.createReasoningGoal(organizationKey, projectKey, userId, textContent)
      const prompt = this.reasoningPrompt + reasoningGoal + "\n\n\nContent to reason about: \n\n" + textContent
      const response = await this.getResponse(organizationKey, projectKey, prompt, userId, this.llmLimit)
      const jsonResponse = this.search.extractJsonFromString(response.responseText)
      jsonResponse.forEach((item: any) => {
        item.iteration = 100
      })
      ontology = ontology.concat(jsonResponse)
      await this.firestoreService.addOntology(userId, organizationKey, projectKey, contentItem.key, "content", ontology)
      ontologies.push(ontology)
    }
    return ontologies
  }

  async createReasoningGoal(organizationKey: string, projectKey: string, userId: string, content: string) {
    const prompt = this.reasoningGoalPrompt + content
    const response = await this.getResponse(organizationKey, projectKey, prompt, userId, this.llmLimit, false, "gemini-2.5-flash-lite")
    return response
  }

  convertJsonToSentences(json: any[]) {
    let sentences: string = ""
    json.forEach((item: any) => {
      const sentence = " " + (item.first_term || "") + " " + (item.relation || "") + " " + (item.second_term || "") + "  \n\n "
      sentences += sentence 
    })

    return sentences
  }

  async getEmbedding(prompt: string) {
    if (!prompt || prompt.length < 10) return []
    const chunk = [{"key": "000", "chunk": prompt}]
    const embedding = await this.geminiService.getGeminiEmbeddings(chunk)
    return embedding
  }
 
  async generateDeduction(organizationKey: string, sourceMinIteration: number, userId: string, threshold: number) {
    const statements = await this.firestoreService.getOntologyStatementsByIteration(organizationKey, sourceMinIteration, sourceMinIteration)
    console.log("deduction input statements: ",statements.length, " threshold: ", threshold, " sourceMinIteration: ", sourceMinIteration)
    let maxStatements = statements.length
    let count = 0
    let promises: Promise<any>[] = []
    for (const statement of statements) {
      if (statement.secondTerm && statement.iteration >= sourceMinIteration) {
        const similarStatements = await this.searchSimilarOntologies(organizationKey, statement, threshold, sourceMinIteration)
        const maxLength = statement.length
        const documentKey = statement.documentKey
        const projectKey = statement.projectKey
        const ontology: OntologyStatement[] = []
        for (const similarStatement of similarStatements) {
          const newRelation: string = " => " + statement.relation + " => " + statement.secondTerm +" => " + similarStatement.relation + " => " + similarStatement.firstTerm // combine original relation and second term with the new relation and first term
          const relationArray = statement.relationArray || []
          relationArray.push({value: statement.firstTerm, order: relationArray.length + 1, iteration: statement.iteration, key: statement.key})          
          relationArray.push({value: statement.relation, order: relationArray.length + 1, iteration: statement.iteration, key: statement.key})
          relationArray.push({value: statement.secondTerm, order: relationArray.length + 1, iteration: statement.iteration, key: statement.key})
          relationArray.push({value: similarStatement.firstTerm, order: relationArray.length + 1, iteration: similarStatement.iteration, key: similarStatement.key})
          relationArray.push({value: similarStatement.relation, order: relationArray.length + 1, iteration: similarStatement.iteration, key: similarStatement.key})
          relationArray.push({value: similarStatement.secondTerm, order: relationArray.length + 1, iteration: similarStatement.iteration, key: similarStatement.key})

          ontology.push({
            first_term: statement.firstTerm, // original first term
            relationArray: relationArray,
            relation: newRelation,
            second_term: similarStatement.secondTerm, // new second term
            documentKey: statement.documentKey, // original document key
            projectKey: statement.projectKey, // original project key
            iteration: statement.iteration + 10, // original iteration
            key: this.firestoreService.generateUID("statement-"), // generate a new key for the new statement
            score: similarStatement.score, // score from the similar statement
            sourceStatementKey: statement.key, // original statement key
            conclusionStatementKey: similarStatement.key // similar statement key
          })
        }
        if (ontology.length > 0) {
          promises.push(this.addOntologyEmbeddings(userId, organizationKey, projectKey, documentKey, ontology, maxLength))
          count += ontology.length
          console.log("progress: ", (count / maxStatements) * 100, "%")
        }

        if (count >= maxStatements) { // stop after maxStatements statements are processed
          break
        }
      }
    }
    await Promise.all(promises)
    return count
  }

  async searchSimilarOntologies(organizationKey: string, statement: any, threshold: number = 0.9, sourceMinIteration: number = 0) {
    const secondTermEmbedding = statement.secondTermEmbedding;
    const id = statement.key;
    if (!secondTermEmbedding || secondTermEmbedding.length === 0) return [];
    // 1. Get similarity results
    let similarityResults = await this.getSimilarOntologies(organizationKey, secondTermEmbedding, 20, threshold, ["first_term"]);
    similarityResults = similarityResults.filter((item: any) => item.id != id);

    if (similarityResults.length === 0) return [];
    // 2. Fetch from Firestore
    const rawStatements = await this.firestoreService.getOntologyStatements(organizationKey, similarityResults.map((item: any) => item.id));

    // 3. Process and Score using .map() to avoid reference issues
    const processedStatements = rawStatements
        .filter((item: any) => item.iteration >= sourceMinIteration)
        .map((item: any) => {
            const embeddingScore = similarityResults.find((res: any) => res.id == item.key)?.score || 1;
            const originScore = item.score || 0;
            
            // Return a NEW object (deep copy via spread)
            return {
                ...item,
                score: embeddingScore * originScore
            };
        });

    return processedStatements.sort((a: any, b: any) => b.score - a.score);
  }

  async createStatements(organizationKey: string, projectKey: string, userId: string, documentKey: string) {
    const content = await this.firestoreService.getDocumentContent(organizationKey, documentKey)
    const textContent = content.textContent.substring(0, 200000)
    const chunks = this.decomposeText(textContent, 7000)
    let response: any[] = []
    let count = 0
    const ontology: OntologyStatement[] = []
    for (const chunk of chunks) {
      const prompt = this.statementPrompt + "\n\n\nContent: \n\n" + chunk
      const statements = await this.getResponse(organizationKey, projectKey, prompt, userId, this.llmLimit, false, "gemini-2.5-flash-lite", 0)
      let responseJson = this.search.extractJsonFromString(statements.responseText)
      if (!responseJson || responseJson.length == 0) { // if response is empty or wrong, try again
       const statements = await this.getResponse(organizationKey, projectKey, prompt, userId, this.llmLimit, false, "gemini-2.5-flash-lite", 0)
       responseJson = this.search.extractJsonFromString(statements.responseText)
      }
      if (responseJson && responseJson.length > 0) {
        responseJson.forEach((item: any) => {
          item.key = this.firestoreService.generateUID("statement-")
        })
      }
      response = response.concat(responseJson) 
      count += 1
      console.log("progress: ", (count / chunks.length) * 100, "%")
    }

    for (const item of response) {
    ontology.push({
      first_term: "", // original first term
      relationArray: [],
      relation: "",
      second_term: item.statement, // new second term
      documentKey: documentKey, // original document key
      projectKey: projectKey, // original project key
      iteration: -1, // original iteration
      key: item.key, // generate a new key for the new statement
      score: 1, // score from the similar statement
      sourceStatementKey: item.key, // original statement key
      conclusionStatementKey: item.key // similar statement key
    })
  }
console.log("ontology: ", ontology)
  const maxLength = ontology.length
  
  if (ontology.length > 0) {
    await this.addOntologyEmbeddings(userId, organizationKey, projectKey, documentKey, ontology, maxLength)
  }
    return response
  }



  decomposeText(text: string, targetLength: number = 1000): string[] {
    const chunks: string[] = [];
    let remainingText = text.trim();
  
    while (remainingText.length > 0) {
      if (remainingText.length <= targetLength) {
        chunks.push(remainingText);
        break;
      }
  
      // 1. Establish a search window
      let splitIndex = -1;
      const chunkWindow = remainingText.substring(0, targetLength);
  
      // 2. Try to find the best break point in descending priority
      // Priority A: Paragraphs
      const lastParagraph = chunkWindow.lastIndexOf('\n\n');
      // Priority B: Sentences (Period, Question mark, or Exclamation followed by space)
      const lastSentence = chunkWindow.search(/[.!?]\s(?!.*[.!?]\s)/); 
      // Priority C: Last Space (to avoid breaking words)
      const lastSpace = chunkWindow.lastIndexOf(' ');
  
      if (lastParagraph > targetLength * 0.5) {
        splitIndex = lastParagraph + 2; // +2 to include the newlines
      } else if (lastSentence !== -1 && lastSentence > targetLength * 0.3) {
        splitIndex = lastSentence + 2; // +2 to include the punctuation and following space
      } else if (lastSpace !== -1) {
        splitIndex = lastSpace + 1;
      } else {
        // Fallback: Hard cut if no spaces/punctuation found
        splitIndex = targetLength;
      }
  
      chunks.push(remainingText.substring(0, splitIndex).trim());
      remainingText = remainingText.substring(splitIndex).trim();
    }
  
    return chunks;
  }






  // Proof statement
  async proofStatement(organizationKey: string, userStatement: string, threshold: number = 0.9) {
    const negatedStatement = this.processText(userStatement)
    console.log("negatedStatement: ", negatedStatement)
    const userEmbedding = await this.getEmbedding(negatedStatement)
    const similarStatements = await this.getSimilarOntologies(organizationKey, userEmbedding[0].values, 50, threshold, ["second_term"])
    if (similarStatements.length === 0) return []
    const ids = similarStatements.map((item: any) => item.id)
    let foundStatement: any = {}
    for (const id of ids) { // I need to iterate over vectors, cause there are vectors also from other documents
      foundStatement = await this.firestoreService.getOntologyStatement(organizationKey, id) // take only the first statement
      console.log("foundStatement 1: ", foundStatement)
      if (foundStatement) {
        foundStatement.similarity = similarStatements.find((res: any) => res.id == foundStatement.key)?.score || 1;
        return foundStatement
      }
    }
    if (!foundStatement) return []
  }






  private triggers = ['nie', 'brak', 'nigdy', 'ani', 'żaden', 'nigdzie', 'nic', 'brakujący', 'brakująca', 'brakujące', 'brakującego', 'brakującej', 'brakujących', 'no', 'not', 'never', 'no one', 'nobody', 'nothing', 'none', 'not one', 'not any', 'not all', 'not most', 'not some', 'not any of', 'not all of', 'not most of', 'not some of', 'neither', 'nor', 'non', 'nein', 'negative'];
  private triggerPrefixes = ['nie', 'non'];
  

  processText(text: string): string {
    let processedText = text.toLowerCase();
    let isNegation = false;
    const words = processedText.split(/\s+/);

    const transformedWords = words.map(word => {
        let currentWord = word;
        let usedTriggerPrefix: string[] = [];

        for (const trigger of this.triggerPrefixes) {
          // 1. Obsługa prefiksów (np. "niezdrowy")
          // Sprawdzamy, czy słowo ZACZYNA SIĘ od triggera
          if (word.startsWith(trigger) && word.length > trigger.length + 2) {
              currentWord = `NEGATIVE_${word}`;
              isNegation = true; 
              usedTriggerPrefix.push(trigger);
          } 
      }

        for (const trigger of this.triggers) { // processing separeate negative words
            if (word === trigger) {
                currentWord = 'NOT NEGATIVE';
                isNegation = true;
            }
        }
        return currentWord;
    });

    processedText = transformedWords.join(' ');

    // Finalne czyszczenie i globalne wzmocnienie negacji
    processedText = processedText.replace(/\s+/g, ' ').trim();

    return processedText;
}

}

export interface OntologyStatement {
  first_term: string
  relation: string
  relationArray: {"value": string, "order": number, "iteration": number, "key": string}[]
  second_term: string
  key: string
  documentKey: string
  projectKey: string
  iteration: number
  score: number
  sourceStatementKey: string
  conclusionStatementKey: string
}
